import { useCallback, useEffect, useMemo, useState, type CSSProperties, type JSX, type MouseEvent } from 'react'
import {
  api, ApiError, type AccountFilters, type AccountRow, type AccountsPage, type SavedList, type User,
} from '../api.js'
import {
  applyFilters, DEFAULT_FILTERS, FilterRail, industriesOf,
  type FilterState,
} from '../components/FilterRail.js'
import { CHROME_HEIGHT } from '../components/Chrome.js'
import { LIST_GONE, ListsPanel } from '../components/ListsPanel.js'
import {
  characterizationOf, ConfidencePill, FlagPill, CharacterizationLabel, formatDate, formatMoney,
  grouped, KwFigure, StatusPill,
} from '../components/Pill.js'
import { T } from '../tokens.js'

/**
 * The R-16 targeting list: the ranked book, the filter rail, and the four
 * states this screen can be in — loading, refused, empty, and a table.
 *
 * ## What this screen does not do
 *
 * **It does not multiply anything.** `curtailableKwWeighted` arrives from the
 * server, computed from `config.ranking.confidenceWeights` (high 1.0, medium
 * 0.7, low 0.4) against the run's own stamped configuration. The mock computes
 * it in the browser from a hardcoded `{High:1, Medium:0.8, Low:0.3}`, which is
 * wrong twice over: the values differ from the program's, and a figure the
 * browser calculates is a figure no audit can reproduce. `TargetingList.test.tsx`
 * serves a row whose weighted figure could not be any product of its raw one
 * and asserts the served figure is what appears.
 *
 * **It draws no staleness banner.** The mock renders one unconditionally, with
 * a fixed date and a fixed failure. The plan asks for it to be conditional and
 * for its dates to come from the API — and **no route exposes what the
 * condition needs**. `GET /api/accounts` answers `{ runId, total, accounts }`;
 * there is no runs route, nothing carries a run's status, its `completed_at` or
 * its `as_of_date`, and the run this list ranks from is by construction
 * `resolveLatestCompletedRun` — the newest run that *completed*. "The figures
 * are stale" is therefore a claim about a newer run that failed or is still
 * going, which is exactly the fact the API never states. Drawing the banner
 * anyway would put a fabricated date in front of a rep; drawing it never is the
 * only honest reading, and the test file pins it so nobody reinstates the
 * mock's version by transcription.
 *
 * **A saved list is the one filter this screen asks the server for.** Every
 * rail control filters the response in the browser, for the reasons
 * `FilterRail.tsx` sets out — but `?savedListId=` is a real filter the route
 * supports, it resolves the membership against the run's own estimates rather
 * than against whatever the browser is holding, and it is the only way to see a
 * list containing an account the default view suppresses. So selecting a list
 * re-fetches, and `total` then means "accounts in this list", not "eligible".
 *
 * **Both kW figures are on every row (R-16).** A list showing only the weighted
 * figure hides the account's real size; only the raw figure hides the discount.
 * The Weighted column greys on a Low-confidence account, which is the mock's
 * way of showing the discount without a third number.
 */

/**
 * `formatDate` and `formatMoney` were defined here until the account drill-down
 * became their second caller. They live in `components/Pill.js` now, beside
 * `formatKw` and `grouped`, which are there for the same reason: a screen
 * importing a formatter from a *sibling screen* couples two things that have
 * nothing to do with each other.
 */

/**
 * The six brand colours, cycled over the industries present in the book.
 *
 * Decoration, and only decoration: it says "same colour, same industry" and
 * nothing else, which is all the mock's own `TYPECOLOR` map says. It is derived
 * from the sorted industry list rather than hardcoded per NAICS code so that a
 * book with a seventh industry still draws, and it carries no meaning that
 * would be wrong if the assignment changed.
 */
const SWATCHES: readonly string[] = [
  T.color.amber, T.color.cyan, T.color.green, T.color.navy, T.color.lime, T.color.micro,
]

const NO_CONTACT = 'No contact recorded'

const OUTCOME_LABELS = {
  enrolled: 'Enrolled',
  declined: 'Declined',
  no_answer: 'No answer',
  met_follow_up: 'Met — follow up',
} as const

export const EMPTY_HEADING = 'No accounts match these filters.'
export const EMPTY_BODY_CONFIDENCE
  = 'Every confidence band is switched off, so nothing can rank. Turn one back on, or reset to your book.'
/**
 * The other way to empty this list.
 *
 * The plan gives the confidence copy verbatim and it is verbatim above — but it
 * is a *statement about the filters*, and rendering it after a rep picks an
 * industry with no accounts in it would be a false one on screen. So the
 * mandated sentence is shown in the case it describes and this one in every
 * other, rather than one sentence covering a case it does not fit.
 */
export const EMPTY_BODY_GENERAL
  = 'No account in your book matches every filter you have set. Change one, or reset to your book.'
export const RESET_LABEL = 'Reset to my book'

/**
 * The third way this table can be empty, and the only one the filters did not
 * cause: a saved list nobody has put an account into yet.
 *
 * Neither of the other two sentences fits. "Every confidence band is switched
 * off" is false, and "No account in your book matches every filter you have
 * set" blames the rail for a list that is simply empty — which sends a rep to
 * reset filters that were never the problem.
 */
export const EMPTY_BODY_EMPTY_LIST
  = 'This saved list has no accounts in it yet. Switch to your book, filter it down, and add them here.'

const NO_RUN_MESSAGE
  = 'No scoring run has completed yet, so there is nothing to rank. Ask an analyst to score the book.'

/**
 * Two 404s this screen has to tell apart, plus everything else.
 *
 * `GET /api/accounts` answers 404 `no_completed_run` when nothing has been
 * scored and 404 `saved_list_not_found` when `?savedListId=` names a list that
 * does not exist **or** one this caller may not read — deliberately the same
 * answer for both, so the route is not an existence oracle for list ids. That
 * is precisely why `LIST_GONE` says "no longer available" and never "you do not
 * have permission": the caller cannot tell which of the two it was, and neither
 * can this screen. The 403 wording lives in `ListsPanel`, on the one route that
 * is allowed to send one.
 */
function failureMessage(error: ApiError): string {
  if (error.code === 'no_completed_run') return NO_RUN_MESSAGE
  if (error.code === 'saved_list_not_found') return LIST_GONE
  return error.message
}

/**
 * The header row, and it is `position: sticky` on the **cells**.
 *
 * Sticky on `<thead>` or on its `<tr>` is the obvious spelling and it is the
 * one browsers disagree about; sticky on each `<th>` is the spelling all of
 * them implement. `top: 0` is the top of `table-pane`, which is the nearest
 * scrolling ancestor.
 *
 * **The ground is not decoration.** A sticky header with a transparent
 * background is a header rows scroll *through*: nine column names laid over
 * account names, which is worse than no sticky header at all. `zIndex: 1` puts
 * it over the rows for the same reason — both are positioned once one of them
 * is sticky, and without it document order wins and the rows paint on top.
 */
const headerCell: CSSProperties = {
  padding: '12px 14px',
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: '0.1em',
  textTransform: T.micro.transform,
  color: T.color.micro,
  borderBottom: `1.5px solid ${T.color.rule}`,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: '#fff',
}

/**
 * `borderBottom` is on the cell, not on the `<tr>` it used to be on.
 *
 * The table is `borderCollapse: 'separate'` now — under `collapse` a sticky
 * `<th>`'s own border belongs to the collapsed grid rather than to the cell, so
 * it stays behind with the table and the header scrolls away from its own rule.
 * And under `separate` a border declared on a `<tr>` is not rendered at all
 * (CSS 2.1 §17.6.1: in the separated model, rows have no borders), so the row
 * rules had to move down here to survive the switch. Same 1px hairline, same
 * place on screen.
 */
const bodyCell: CSSProperties = {
  padding: '13px 14px',
  verticalAlign: 'top',
  borderBottom: `1px solid ${T.color.hairline}`,
}

const subLine: CSSProperties = {
  fontSize: 12,
  color: T.color.micro,
  marginTop: 2,
}

type Load =
  | { phase: 'loading' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; page: AccountsPage }

export interface TargetingListProps {
  /** The signed-in user. `ListsPanel` needs the id and the role; nothing else here reads it. */
  user: User
  /** Called with the account id when a rep opens a row, by link or by click. */
  onOpen: (accountId: string) => void
}

export function TargetingList({ user, onOpen }: TargetingListProps): JSX.Element {
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS)
  const [selected, setSelected] = useState<SavedList | null>(null)
  const selectedId = selected === null ? null : selected.id

  useEffect(() => {
    // No saved list: one request with no parameters at all, which is the
    // program's default view, decline suppression and all (R-16 AC-3). With a
    // saved list: `?savedListId=` and nothing else. Every *rail* control still
    // filters what came back — see `FilterRail.tsx` for why.
    //
    // `exactOptionalPropertyTypes` is why this is built rather than spread:
    // `{ savedListId: undefined }` is a different thing from an absent key, and
    // an empty `savedListId` on the wire is a 400 the route added a guard for.
    let cancelled = false
    const query: AccountFilters = {}
    if (selectedId !== null) query.savedListId = selectedId
    void api.accounts(query)
      .then((page) => { if (!cancelled) setLoad({ phase: 'ready', page }) })
      .catch((error: unknown) => {
        if (cancelled) return
        const message = error instanceof ApiError
          ? failureMessage(error)
          : 'Something went wrong loading your book.'
        setLoad({ phase: 'failed', message })
      })
    return () => { cancelled = true }
  }, [selectedId])

  /**
   * Changing scope resets the rail, and that is not tidiness.
   *
   * The industry `<select>`'s options are read off the rows that arrived
   * (`industriesOf`), so a different scope has a different option list. Found
   * in a real browser: filter the book to `Elementary and secondary schools`,
   * then open a saved list holding none — `state.naics` is still `611110`, the
   * select has no such option, and the browser therefore *displays* `Any
   * industry` while the screen filters by an industry the control no longer
   * names. A control that says one thing and does another is worse than a
   * filter a rep has to set again, and no test in jsdom would have caught it,
   * because before this task the rows never changed after the first load.
   */
  const onSelect = useCallback((list: SavedList | null) => {
    setSelected(list)
    setFilters(DEFAULT_FILTERS)
    setLoad({ phase: 'loading' })
  }, [])

  const rows = load.phase === 'ready' ? load.page.accounts : []
  const industries = useMemo(() => industriesOf(rows), [rows])
  const swatches = useMemo(() => {
    const map = new Map<string, string>()
    industries.forEach((industry, index) => {
      map.set(industry.naics, SWATCHES[index % SWATCHES.length] ?? T.color.micro)
    })
    return map
  }, [industries])
  const shown = useMemo(() => applyFilters(rows, filters), [rows, filters])
  const shownAccountIds = useMemo(() => shown.map((row) => row.accountId), [shown])

  const everyBandOff = !filters.confidence.high && !filters.confidence.medium && !filters.confidence.low

  return (
    /*
      **The page does not scroll; the table body does.**

      A rep filters, reads the top of the list, scrolls, and then cannot see
      which filters are on or what the column they are reading is called. So the
      rail and the header row stay put and only the rows move — which takes four
      things, and leaving any one of them out is why "I set overflow and nothing
      happened":

      1. A **bounded height** here. `100vh` less the chrome, from
         `CHROME_HEIGHT`, so this screen is exactly the pane `<main>` gives it
         and never more. `overflow: hidden` is what makes that a promise rather
         than a suggestion.
      2. `minHeight: 0` on **every flex and grid child that scrolls**. The
         automatic minimum size of a flex or grid item is its content, so an item
         holding 281 rows refuses to be shorter than 281 rows and pushes the
         bound out from the inside. This is the step that is invisible when it is
         missing: the CSS all looks right and the page scrolls anyway.
      3. `overflowY: 'auto'` on the rail **and** on the table pane, separately.
         The rail is taller than the viewport with every group open, so a rail
         that could not scroll would simply lose its last section.
      4. `position: sticky` on the `<th>`s, with a ground — see `headerCell`.
    */
    <div
      data-testid="targeting-screen"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${CHROME_HEIGHT}px)`,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderBottom: `1.5px solid ${T.color.rule}`,
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 16,
          // The one band that is neither of the two scrolling panes. Without
          // this it is a flex item like they are and shrinks with them.
          flexShrink: 0,
        }}
      >
        {/*
          The mock's title is `My week — West Valley book`. There is no
          territory in this data model and no saved list is applied here (lists
          are Task 13), so "West Valley" and "Saved list ·" would both be
          claims the system cannot support — the same reason Task 11 dropped the
          mock's `M. Reyes · West Valley` and Task 10 dropped its inert `keep me
          signed in`.
        */}
        <h1 style={{ fontSize: 23, fontWeight: T.weight.bold, letterSpacing: '-0.01em', margin: 0 }}>
          My week
        </h1>
        {/*
          `total` is the response's own row count, so it means two different
          things and says which one it means. On the book it is the eligible
          set the program's default view returned; on a saved list it is that
          list's membership as the run resolved it — which can be smaller than
          the count in the panel, because an account with no estimate in this
          run has no row to return. Both figures are shown rather than
          reconciled, for the same reason Sky Harbor keeps its estimate and its
          nomination.
        */}
        <div aria-live="polite" style={{ fontSize: 12, color: T.color.muted }}>
          {load.phase !== 'ready'
            ? (selected === null ? 'Your book' : `Saved list · ${selected.name}`)
            : selected === null
              ? `Your book · ${grouped(load.page.total, 0)} eligible · ${grouped(shown.length, 0)} shown`
              : `Saved list · ${selected.name} · ${grouped(load.page.total, 0)} in this list`
                + ` · ${grouped(shown.length, 0)} shown`}
        </div>
      </div>

      <div
        data-testid="targeting-layout"
        style={{
          display: 'grid',
          gridTemplateColumns: `${T.rail.filters}px 1fr`,
          alignItems: 'stretch',
          // Takes what the header band left, and `minHeight: 0` lets it be
          // shorter than the 281 rows inside it. Both halves are needed.
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          data-testid="filter-pane"
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: T.rail.filters,
            background: '#fff',
            borderRight: `1.5px solid ${T.color.rule}`,
            // The rail's own scroll. With every group open it is taller than a
            // 900px viewport, and a rail bounded by the grid without a scroll of
            // its own would lose the characterization checkbox off the bottom.
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <ListsPanel
            user={user}
            selected={selected}
            onSelect={onSelect}
            shownAccountIds={shownAccountIds}
          />
          <FilterRail
            state={filters}
            industries={industries}
            declineSuppression={selected === null}
            onChange={setFilters}
          />
        </div>
        <div
          data-testid="table-pane"
          style={{
            background: '#fff',
            minWidth: 0,
            // The pane that actually scrolls the book. `minHeight: 0` for the
            // same reason as everywhere else on this screen; `minWidth: 0` was
            // already here and is the horizontal twin of it.
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {load.phase === 'loading' ? <Notice>Loading your book…</Notice> : null}
          {load.phase === 'failed' ? <Notice>{load.message}</Notice> : null}
          {load.phase === 'ready' && shown.length === 0
            ? (
              <EmptyState
                everyBandOff={everyBandOff}
                emptyList={selected !== null && load.page.total === 0}
                onReset={() => { setFilters(DEFAULT_FILTERS) }}
              />
            )
            : null}
          {load.phase === 'ready' && shown.length > 0
            ? <AccountsTable rows={shown} swatches={swatches} onOpen={onOpen} />
            : null}
        </div>
      </div>
    </div>
  )
}

function Notice({ children }: { children: string }): JSX.Element {
  return (
    <p style={{ margin: 22, fontSize: 13.5, color: T.color.body, maxWidth: '60ch' }}>{children}</p>
  )
}

interface EmptyStateProps {
  everyBandOff: boolean
  /** The response itself was empty and a list is applied — the filters are not the reason. */
  emptyList: boolean
  onReset: () => void
}

/**
 * The heading is mandated verbatim and says "these filters", which is true of
 * two of the three bodies. An empty saved list is not a filter result, so it
 * gets its own heading as well — telling a rep their filters excluded
 * everything, when the list they picked contains nothing at all, sends them to
 * fix a rail that is working.
 */
export const EMPTY_HEADING_EMPTY_LIST = 'This list is empty.'

function EmptyState({ everyBandOff, emptyList, onReset }: EmptyStateProps): JSX.Element {
  return (
    <div style={{ margin: 22, border: `1.5px dashed ${T.color.border}`, padding: 26 }}>
      <div style={{ fontSize: 16, fontWeight: T.weight.bold }}>
        {emptyList ? EMPTY_HEADING_EMPTY_LIST : EMPTY_HEADING}
      </div>
      <div style={{ fontSize: 13.5, color: T.color.muted, marginTop: 6, maxWidth: '60ch' }}>
        {emptyList ? EMPTY_BODY_EMPTY_LIST : everyBandOff ? EMPTY_BODY_CONFIDENCE : EMPTY_BODY_GENERAL}
      </div>
      {/*
        No reset button on an empty list. The filters are not why it is empty,
        and `Reset to my book` would clear a rail the rep never touched while
        leaving them on the same empty list — a button that appears to do
        nothing. The `My book` control in the panel is the way out, and it is
        already on screen.
      */}
      {emptyList
        ? null
        : (
          <button
            type="button"
            onClick={onReset}
            style={{
              marginTop: 16,
              border: `1.5px solid ${T.color.navy}`,
              background: T.color.navy,
              color: '#fff',
              padding: '10px 14px',
              fontFamily: T.fontFamily,
              fontSize: T.micro.size,
              fontWeight: T.weight.bold,
              letterSpacing: '0.08em',
              textTransform: T.micro.transform,
              borderRadius: T.radius.button,
              cursor: 'pointer',
            }}
          >
            {RESET_LABEL}
          </button>
        )}
    </div>
  )
}

interface TableProps {
  rows: readonly AccountRow[]
  swatches: ReadonlyMap<string, string>
  onOpen: (accountId: string) => void
}

function AccountsTable({ rows, swatches, onOpen }: TableProps): JSX.Element {
  return (
    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
      <caption style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        Accounts in your book, ranked
      </caption>
      <thead>
        <tr>
          <th scope="col" style={{ ...headerCell, textAlign: 'left' }}>Account</th>
          <th scope="col" style={{ ...headerCell, textAlign: 'left' }}>Type</th>
          <th scope="col" style={{ ...headerCell, textAlign: 'right' }}>Curtailable kW</th>
          <th scope="col" style={{ ...headerCell, textAlign: 'right' }}>Weighted</th>
          <th scope="col" style={{ ...headerCell, textAlign: 'left' }}>Confidence</th>
          <th scope="col" style={{ ...headerCell, textAlign: 'right' }}>Season value</th>
          <th scope="col" style={{ ...headerCell, textAlign: 'left' }}>Status</th>
          <th scope="col" style={{ ...headerCell, textAlign: 'left' }}>Last contact</th>
          {/*
            Its own column, and deliberately not folded into `Last contact`.
            That column means `priorSeasonOutcome` and `lastDeclinedAt` —
            enrollment contact history — and a rep reading "last contact: 3 days
            ago" under a heading that could mean either would have no way to
            tell which fact they were looking at.
          */}
          <th scope="col" style={{ ...headerCell, textAlign: 'left' }}>Notes</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <Row key={row.accountId} row={row} swatch={swatches.get(row.naics)} onOpen={onOpen} />
        ))}
      </tbody>
    </table>
  )
}

interface RowProps {
  row: AccountRow
  swatch: string | undefined
  onOpen: (accountId: string) => void
}

/** An unmodified primary click — the only kind this screen is allowed to intercept. */
function isPlainClick(event: MouseEvent<HTMLElement>): boolean {
  return !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0
}

function Row({ row, swatch, onOpen }: RowProps): JSX.Element {
  // The id of the cell carrying this row's confidence label. `KwFigure`'s
  // `label="adjacent"` mode *requires* it — display rule 1 says a kW figure
  // never appears without its confidence, and "the label is in the next cell
  // but three" has to name the cell it means so that a screen reader on the
  // number hears the label too.
  const confidenceId = `confidence-${row.accountId}`

  const openFromLink = (event: MouseEvent<HTMLAnchorElement>): void => {
    // A modified click is the rep asking the browser for a new tab or window,
    // and it is left alone. That is the whole reason this is an `<a href>` and
    // not a `<div onClick>`.
    if (!isPlainClick(event)) return
    event.preventDefault()
    event.stopPropagation()
    onOpen(row.accountId)
  }

  return (
    <tr
      // The row check is not belt and braces. Without it a cmd-click on the
      // account name bubbles out of the link — which correctly stood aside —
      // into this handler, and the rep gets the drill-down in *this* tab as
      // well as the new one they asked for. The test that pins the link's
      // behaviour is what found it.
      onClick={(event) => { if (isPlainClick(event)) onOpen(row.accountId) }}
      // The row rule lives on `bodyCell` now — see the note there. A `<tr>`
      // border is silently dropped under `borderCollapse: separate`, which is
      // what the sticky header needed.
      style={{ cursor: 'pointer' }}
    >
      <td style={bodyCell}>
        <a
          href={`/accounts/${encodeURIComponent(row.accountId)}`}
          onClick={openFromLink}
          style={{ fontSize: 14, fontWeight: T.weight.semi, color: T.color.ink }}
        >
          {row.name}
        </a>
        <div style={subLine}>{row.accountNumber}</div>
      </td>

      <td style={{ ...bodyCell, fontSize: 13 }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 9,
            height: 9,
            background: swatch ?? T.color.micro,
            marginRight: 8,
          }}
        />
        {row.naicsLabel}
        <div style={{ fontSize: 12, marginTop: 2 }}>
          <CharacterizationLabel {...characterizationOf(row)} />
        </div>
      </td>

      <td style={{ ...bodyCell, textAlign: 'right', fontSize: 15 }}>
        <KwFigure
          kw={row.curtailableKwRaw}
          confidence={row.confidence}
          label="adjacent"
          confidenceLabelId={confidenceId}
          testId="kw-raw"
        />
      </td>

      <td style={{ ...bodyCell, textAlign: 'right', fontSize: 15 }}>
        <KwFigure
          kw={row.curtailableKwWeighted}
          confidence={row.confidence}
          label="adjacent"
          confidenceLabelId={confidenceId}
          tone={row.confidence === 'low' ? 'discounted' : 'ink'}
          testId="kw-weighted"
        />
      </td>

      <td id={confidenceId} style={bodyCell}>
        <ConfidencePill confidence={row.confidence} />
        {/*
          The flag pill marks a cohort outlier, and nothing else. Flag
          *presence* is not a targeting signal on this book — 201 of the 281
          rows in the default view carry at least one flag, most of them the
          internal `der_unknown`, and a red badge on 72% of a list is not a
          badge. A cohort outlier is the one that means "verify this number
          before you quote it", which is what `FlagPill` was written for. Every
          code the account carries is in the `title`, so nothing is hidden — and
          the pill still says `Flag`, never the flag's text, because flag text
          is internal (display rule 7).
        */}
        {row.cohortOutlier
          ? (
            <span style={{ marginLeft: 5 }}>
              <FlagPill title={[...row.flagCodes, ...row.internalFlagCodes].join(', ')} />
            </span>
          )
          : null}
      </td>

      <td style={{ ...bodyCell, textAlign: 'right', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(row.seasonValueTotal)}
      </td>

      <td style={bodyCell}>
        <StatusPill status={row.enrollmentStatus} />
      </td>

      <td style={{ ...bodyCell, fontSize: 13 }}>
        <LastContact row={row} />
      </td>

      <td style={{ ...bodyCell, fontSize: 13 }}>
        <Notes row={row} />
      </td>
    </tr>
  )
}

/** Grey body text, the way display rule 4 renders every other normal absence. */
export const NO_NOTES_ON_ROW = 'No notes'

/**
 * The count and the recency spec §7.7 puts on the targeting list row.
 *
 * **Both, not one.** A count alone cannot separate an account somebody wrote up
 * this morning from one nobody has touched since March, and a date alone hides
 * whether that date is the whole record or the latest of nine. The design brief
 * asks for the pair by name — "note count and recency belong on the targeting
 * list row" — because surfacing is the whole problem the notes feature has.
 *
 * **`noteCount` is the server's number and is not recomputed here.** The row
 * also carries `hasNotes`, which the server derives from that same count, so
 * this cell reads the count and lets the two agree by construction rather than
 * asking a second question and hoping.
 *
 * The absent case is two grey words, not an empty cell and not a prompt to
 * write one: an account nobody has written about is the normal state of a
 * targeting list, which is the same argument `LastContact` above and display
 * rule 4 both make.
 */
function Notes({ row }: { row: AccountRow }): JSX.Element {
  if (row.noteCount === 0) {
    return (
      <span data-testid="notes-cell" data-note-count="0" style={{ color: T.color.micro }}>
        {NO_NOTES_ON_ROW}
      </span>
    )
  }
  return (
    <span data-testid="notes-cell" data-note-count={String(row.noteCount)} style={{ display: 'block' }}>
      {`${grouped(row.noteCount, 0)} ${row.noteCount === 1 ? 'note' : 'notes'}`}
      {/*
        `mostRecentNoteAt` is null only when the count is 0, which this branch
        has already excluded — but the type says `string | null` and a cell that
        printed `null` would be worse than one that prints nothing, so the guard
        stands rather than being asserted away with a `!`.
      */}
      {row.mostRecentNoteAt === null
        ? null
        : <span style={{ ...subLine, display: 'block' }}>{formatDate(row.mostRecentNoteAt)}</span>}
    </span>
  )
}

/**
 * The most recent recorded outcome for this account, and its date if the API
 * carries one.
 *
 * **It usually does not, and that is a gap worth naming.** `AccountListRow`
 * has `priorSeasonOutcome` (the most recent `rep_outcomes.result`) and
 * `lastDeclinedAt` (the most recent *decline's* date) and no general "last
 * approached" date at all — so an account approached and enrolled shows an
 * outcome with no date, and only a decline shows both. On the seeded book,
 * `priorSeasonOutcome` is `enrolled` for exactly the 37 enrolled accounts and
 * `null` for the other 244, so this column repeats the Status column and adds
 * nothing; under `includeDeclined` it earns its place. Carried as a finding for
 * Task 15 and for Plan 5's outcome capture.
 *
 * The absent case is two grey words, not an empty cell and not a prompt: an
 * account nobody has called yet is the normal state of a targeting list, which
 * is the same argument display rule 4 makes about characterization.
 */
function LastContact({ row }: { row: AccountRow }): JSX.Element {
  if (row.priorSeasonOutcome === null && row.lastDeclinedAt === null) {
    return <span data-testid="last-contact" style={{ color: T.color.micro }}>{NO_CONTACT}</span>
  }
  return (
    <span data-testid="last-contact" style={{ display: 'block' }}>
      {row.priorSeasonOutcome === null ? NO_CONTACT : OUTCOME_LABELS[row.priorSeasonOutcome]}
      {row.lastDeclinedAt === null
        ? null
        : <span style={{ ...subLine, display: 'block' }}>{formatDate(row.lastDeclinedAt)}</span>}
    </span>
  )
}
